import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getApiUrl, getAuthHeaders } from '../utils/api';
import './AdminGifts.css';
import './AdminPurchaseDetail.css';

interface PurchaseGiftItem {
  id?: string;
  nome: string;
  descricao?: string;
  preco: number;
  quantidade: number;
  imagem?: string;
}

interface PurchaseDetail {
  id: string;
  fromName: string;
  email: string;
  message: string;
  gifts: PurchaseGiftItem[];
  totalPrice: number;
  paymentId: string | null;
  status?: 'pending' | 'approved' | 'rejected';
  createdAt?: unknown;
  pendingEmailSentToBuyer?: boolean;
  confirmationEmailSentToBuyer?: boolean;
  emailSentToBrides?: boolean;
  rejectedEmailSentToBuyer?: boolean;
  thankYouEmailSentToBuyer?: boolean;
  thankYouMessage?: string;
  thankYouSentAt?: unknown;
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

const formatBRL = (n: number): string =>
  `R$ ${(Number.isFinite(n) ? n : 0).toFixed(2).replace('.', ',')}`;

const formatDate = (raw: unknown): string => {
  if (!raw) return '—';
  // Firestore Timestamp serialized as { _seconds, _nanoseconds }
  if (typeof raw === 'object' && raw !== null) {
    const r = raw as { _seconds?: number; seconds?: number };
    const seconds = r._seconds ?? r.seconds;
    if (typeof seconds === 'number') {
      return new Date(seconds * 1000).toLocaleString('pt-BR');
    }
  }
  if (typeof raw === 'string' || typeof raw === 'number') {
    const d = new Date(raw);
    if (!isNaN(d.getTime())) return d.toLocaleString('pt-BR');
  }
  return '—';
};

const statusLabel = (s?: string): string =>
  s === 'approved' ? 'Aprovado' : s === 'rejected' ? 'Recusado' : 'Pendente';

const AdminPurchaseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [purchase, setPurchase] = useState<PurchaseDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [thankYouMessage, setThankYouMessage] = useState<string>('');
  const [sending, setSending] = useState<boolean>(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdCounter = React.useRef<number>(0);

  const showToast = (message: string, type: 'success' | 'error') => {
    const tid = toastIdCounter.current++;
    setToasts((prev) => [...prev, { id: tid, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== tid));
    }, 5000);
  };

  useEffect(() => {
    if (!id) return;
    const fetchPurchase = async () => {
      setLoading(true);
      setErrorMsg(null);
      try {
        const response = await fetch(getApiUrl(`payment/purchase/${id}`), {
          headers: getAuthHeaders(),
        });
        if (!response.ok) {
          throw new Error('Falha ao carregar compra');
        }
        const data = (await response.json()) as PurchaseDetail;
        setPurchase(data);
      } catch (err) {
        console.error('Error fetching purchase:', err);
        setErrorMsg(err instanceof Error ? err.message : 'Erro desconhecido');
      } finally {
        setLoading(false);
      }
    };
    fetchPurchase();
  }, [id]);

  const handleSendThankYou = async () => {
    if (!purchase) return;
    const msg = thankYouMessage.trim();
    if (!msg) {
      showToast('Escreva uma mensagem antes de enviar.', 'error');
      return;
    }
    setSending(true);
    try {
      const response = await fetch(getApiUrl('payment/send-thank-you'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ purchaseId: purchase.id, message: msg }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.ok) {
        throw new Error(result?.error || 'Falha ao enviar agradecimento');
      }
      setPurchase((prev) =>
        prev
          ? {
              ...prev,
              thankYouEmailSentToBuyer: true,
              thankYouMessage: msg,
              thankYouSentAt: new Date().toISOString(),
            }
          : prev,
      );
      showToast('Agradecimento enviado com sucesso!', 'success');
    } catch (err) {
      console.error('Error sending thank-you:', err);
      showToast(
        `Erro ao enviar agradecimento: ${err instanceof Error ? err.message : 'Erro desconhecido'}`,
        'error',
      );
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-gifts">
        <div style={{ padding: '20px', textAlign: 'center' }}>Carregando compra...</div>
      </div>
    );
  }

  if (errorMsg || !purchase) {
    return (
      <div className="admin-gifts">
        <div className="purchase-detail-page">
          <button
            type="button"
            className="purchase-detail-back"
            onClick={() => navigate('/admin/gifts')}
          >
            ← Voltar para Comprados
          </button>
          <div style={{ padding: '20px', textAlign: 'center', color: '#c62828' }}>
            {errorMsg || 'Compra não encontrada.'}
          </div>
        </div>
      </div>
    );
  }

  const status = purchase.status || 'pending';
  const alreadyThanked = purchase.thankYouEmailSentToBuyer === true;

  const emailFlags: Array<{ key: keyof PurchaseDetail; label: string }> = [
    { key: 'pendingEmailSentToBuyer', label: 'Pendente → comprador' },
    { key: 'confirmationEmailSentToBuyer', label: 'Confirmação → comprador' },
    { key: 'emailSentToBrides', label: 'Notificação → noivos' },
    { key: 'rejectedEmailSentToBuyer', label: 'Recusado → comprador' },
    { key: 'thankYouEmailSentToBuyer', label: 'Agradecimento → comprador' },
  ];

  return (
    <div className="admin-gifts">
      <div className="purchase-detail-page">
        <button
          type="button"
          className="purchase-detail-back"
          onClick={() => navigate('/admin/gifts')}
        >
          ← Voltar para Comprados
        </button>

        <div className="purchase-detail-page-header">
          <h1 className="purchase-detail-page-title">Detalhes da compra</h1>
          <span className={`payment-status payment-status--${status}`}>
            <span className="payment-status-label">{statusLabel(status)}</span>
          </span>
        </div>

        <div className="purchase-detail-card">
          <div className="purchase-detail-row">
            <span className="purchase-detail-label">De:</span>
            <span className="purchase-detail-value">{purchase.fromName || '—'}</span>
          </div>
          <div className="purchase-detail-row">
            <span className="purchase-detail-label">E-mail:</span>
            <span className="purchase-detail-value">{purchase.email || '—'}</span>
          </div>
          <div className="purchase-detail-row">
            <span className="purchase-detail-label">Identificador do pagamento:</span>
            <span className="purchase-detail-value">{purchase.paymentId || '—'}</span>
          </div>
          <div className="purchase-detail-row">
            <span className="purchase-detail-label">Data:</span>
            <span className="purchase-detail-value">{formatDate(purchase.createdAt)}</span>
          </div>
          {purchase.message && (
            <div className="purchase-detail-section">
              <span className="purchase-detail-label">Mensagem aos noivos:</span>
              <p className="purchase-detail-message">{purchase.message}</p>
            </div>
          )}
        </div>

        <div className="purchase-detail-card">
          <h2 className="purchase-detail-section-title">Presentes</h2>
          <ul className="purchase-detail-order-items">
            {purchase.gifts.map((g, i) => (
              <li key={`${g.id || g.nome}-${i}`} className="purchase-detail-order-item">
                {g.imagem ? (
                  <img src={g.imagem} alt={g.nome} className="purchase-detail-order-image" />
                ) : (
                  <div className="purchase-detail-order-image-placeholder">Sem imagem</div>
                )}
                <div className="purchase-detail-order-info">
                  <div className="purchase-detail-order-name">{g.nome}</div>
                  {g.descricao && (
                    <div className="purchase-detail-order-desc">{g.descricao}</div>
                  )}
                  <div className="purchase-detail-order-meta">
                    {formatBRL(Number(g.preco))} × {g.quantidade}
                  </div>
                </div>
                <div className="purchase-detail-order-subtotal">
                  {formatBRL(Number(g.preco) * (g.quantidade ?? 1))}
                </div>
              </li>
            ))}
          </ul>
          <div className="purchase-detail-order-total">
            <span>Total</span>
            <span>{formatBRL(purchase.totalPrice ?? 0)}</span>
          </div>
        </div>

        <div className="purchase-detail-card">
          <h2 className="purchase-detail-section-title">Status dos e-mails</h2>
          <ul className="email-flags-list">
            {emailFlags.map((f) => {
              const sent = purchase[f.key] === true;
              return (
                <li key={String(f.key)} className="email-flag-row">
                  <span
                    className={`email-flag-indicator ${
                      sent ? 'email-flag-indicator--on' : 'email-flag-indicator--off'
                    }`}
                    aria-hidden
                  >
                    {sent ? '✓' : '○'}
                  </span>
                  <span className="email-flag-label">{f.label}</span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="purchase-detail-card">
          <h2 className="purchase-detail-section-title">Responder com agradecimento</h2>
          {alreadyThanked ? (
            <div className="thank-you-sent-box">
              {purchase.thankYouMessage && (
                <p className="thank-you-sent-message">{purchase.thankYouMessage}</p>
              )}
              <div className="thank-you-sent-meta">
                Agradecimento enviado{purchase.thankYouSentAt ? ` em ${formatDate(purchase.thankYouSentAt)}` : ''}.
              </div>
            </div>
          ) : (
            <div className="thank-you-form">
              <textarea
                className="form-textarea thank-you-textarea"
                rows={8}
                value={thankYouMessage}
                onChange={(e) => setThankYouMessage(e.target.value)}
                placeholder="Escreva uma mensagem pessoal de agradecimento..."
                disabled={sending}
                required
              />
              <div className="thank-you-actions">
                <button
                  type="button"
                  className="form-button save-button"
                  onClick={handleSendThankYou}
                  disabled={sending || !thankYouMessage.trim()}
                >
                  {sending ? 'Enviando...' : 'Enviar agradecimento'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="toast-container">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`toast toast-${toast.type}`}
            onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminPurchaseDetail;
